import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Plus, UserPlus, X } from 'lucide-react';
import { useState } from 'react';
import type { SubscriberListRef } from '../api/subscribersApi';
import { COUNTRY_CODES, withCountryCode } from '../model/phone';
import { ListChooser } from './ListChooser';
import { outlineButtonSx, tint } from './styles';

export type NewPerson = { name: string; phone: string; email: string | null; optedIn: boolean; listIds: string[] };

type Props = {
  /** Lists this account may add people to; the one open on the page starts chosen. */
  lists: SubscriberListRef[];
  startList: string;
  isPending: boolean;
  error: string | null;
  onCancel: () => void;
  onAdd: (person: NewPerson) => void;
};

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

type Fields = { name: string; code: string; local: string; email: string; chosen: string[] };

/** The full number once it reads as one, and whether each field is fine to send. */
function checkFields({ name, code, local, email, chosen }: Fields) {
  const phone = withCountryCode(code, local);
  const emailOk = email.trim() === '' || EMAIL.test(email.trim());
  return {
    phone,
    emailOk,
    phoneError: local.replace(/\D/g, '').length > 3 && !phone,
    valid: name.trim() !== '' && phone !== null && emailOk && chosen.length > 0,
  };
}

function ListChips({ lists, chosen, onRemove, onOpen }: { lists: SubscriberListRef[]; chosen: string[]; onRemove: (id: string) => void; onOpen: (el: HTMLElement) => void }) {
  return (
    <Stack direction="row" sx={{ gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
      {chosen.map((id) => {
        const l = lists.find((x) => x.id === id);
        if (!l) return null;
        return (
          <Stack key={id} direction="row" sx={chipSx}>
            {l.name}<Box component="span" sx={{ color: 'text.secondary' }}>{l.optedInCount}</Box>
            <IconButton size="small" aria-label={`Remove ${l.name}`} onClick={() => { onRemove(id); }} sx={{ p: 0.25 }}><X size={12} /></IconButton>
          </Stack>
        );
      })}
      <Button size="small" variant="outlined" startIcon={<Plus size={13} />} onClick={(e) => { onOpen(e.currentTarget); }} sx={outlineButtonSx}>Add to lists</Button>
    </Stack>
  );
}

/** One person, on one or more lists, with their consent recorded. Only opted-in numbers receive messages. */
export function AddSubscriberDialog({ lists, startList, isPending, error, onCancel, onAdd }: Props) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('+65');
  const [local, setLocal] = useState('');
  const [email, setEmail] = useState('');
  const [optedIn, setOptedIn] = useState(true);
  const [chosen, setChosen] = useState<string[]>(startList ? [startList] : []);
  const [chooser, setChooser] = useState<HTMLElement | null>(null);
  const { phone, emailOk, phoneError, valid } = checkFields({ name, code, local, email, chosen });
  const ready = valid && !isPending;

  return (
    <Dialog open onClose={onCancel} fullWidth maxWidth="sm" aria-labelledby="add-sub-title" slotProps={{ paper: { sx: { borderRadius: 2 } } }}>
      <Stack direction="row" sx={{ alignItems: 'flex-start', px: 2.5, pt: 2.5, pb: 1.5 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography id="add-sub-title" variant="h4" component="h2">Add subscriber</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>Only opted-in numbers receive messages.</Typography>
        </Box>
        <IconButton size="small" aria-label="Close" onClick={onCancel}><X size={18} /></IconButton>
      </Stack>
      <Stack sx={{ gap: 2, px: 2.5, pb: 2.5 }}>
        <TextField required label="Name" value={name} onChange={(e) => { setName(e.target.value); }} />
        <Stack direction="row" sx={{ gap: 1 }}>
          <TextField select label="Code" value={code} onChange={(e) => { setCode(e.target.value); }} sx={{ width: 110, flexShrink: 0 }}>
            {COUNTRY_CODES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField required fullWidth label="WhatsApp number" placeholder="9123 4567" value={local} onChange={(e) => { setLocal(e.target.value); }}
            error={phoneError} helperText={phoneError ? 'Check the number for this country code.' : 'Stored in full only on the list; masked everywhere else.'} />
        </Stack>
        <TextField label="Email (optional)" type="email" value={email} onChange={(e) => { setEmail(e.target.value); }} error={!emailOk} helperText={emailOk ? ' ' : 'Check the email address.'} />
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.75 }}>Lists *</Typography>
          <ListChips lists={lists} chosen={chosen} onRemove={(id) => { setChosen((c) => c.filter((x) => x !== id)); }} onOpen={setChooser} />
        </Box>
        <Stack direction="row" component="label" sx={{ gap: 1, p: 1.5, borderRadius: 1.5, bgcolor: tint('text-secondary', 8), cursor: 'pointer' }}>
          <Checkbox size="small" checked={optedIn} onChange={(e) => { setOptedIn(e.target.checked); }} sx={{ p: 0.25, alignSelf: 'flex-start' }} />
          <Box>
            <Typography variant="body2">This person has opted in to receive OCBC research on WhatsApp.</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>The opt-in date is recorded for audit. Without it they are listed but receive nothing.</Typography>
          </Box>
        </Stack>
        {error ? <Typography variant="body2" role="alert" sx={{ color: 'error.main' }}>{error}</Typography> : null}
      </Stack>
      <Stack direction="row" sx={{ justifyContent: 'flex-end', gap: 1, px: 2.5, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Button variant="outlined" onClick={onCancel} sx={outlineButtonSx}>Cancel</Button>
        <Button variant="contained" startIcon={<UserPlus size={15} />} disabled={!ready}
          onClick={() => { if (phone) onAdd({ name: name.trim(), phone, email: email.trim() || null, optedIn, listIds: chosen }); }}>
          Add subscriber
        </Button>
      </Stack>
      {chooser ? (
        <ListChooser anchor={chooser} lists={lists} chosen={chosen} onClose={() => { setChooser(null); }} onApply={(ids) => { setChosen(ids); setChooser(null); }} />
      ) : null}
    </Dialog>
  );
}

const chipSx = {
  alignItems: 'center',
  gap: 0.75,
  pl: 1,
  pr: 0.25,
  py: 0.25,
  borderRadius: 1,
  border: 1,
  borderColor: 'divider',
  fontSize: '0.75rem',
  fontWeight: 500,
} as const;
